import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { testAccuracyService } from '@/feature/testAccuracy/services/testAccuracyService';
import type {
  ITestAccuracyDay,
  ITestAccuracyOrg,
  ITestAccuracyProject,
  ITestAccuracySummary,
  TestAccuracyPeriod,
  TestAccuracySection,
} from '@/feature/testAccuracy/types/testAccuracy';
import { SOURCE_OPTIONS, TEST_TYPE_OPTIONS } from '@/feature/testAccuracy/types/testAccuracy';
import { periodDates, toYmd } from '@/feature/testAccuracy/utils/testAccuracyFormat';
import DateRangeFilter from '@/shared/components/data-display/DateRangeFilter';
import SearchableSelect from '@/shared/components/forms/SearchableSelect';
import Panel from '@/shared/components/ui/Panel';
import SegmentedControl from '@/shared/components/ui/SegmentedControl';

import TestAccuracyDailyTable from './TestAccuracyDailyTable';
import TestAccuracySummary from './TestAccuracySummary';

const ALL_ORGS_ID = 'all';
const AUTO_LOAD_DEBOUNCE_MS = 400;

interface IDashboardDateForm {
  dateFrom: string;
  dateTo: string;
}

const getCustomDateValidationError = (fromDate: string, toDate: string): string | null => {
  if (!fromDate || !toDate) {
    return 'Please select both From and To dates.';
  }
  const today = toYmd(new Date());
  if (toDate > today) {
    return 'To date cannot be after today.';
  }
  if (fromDate > today) {
    return 'From date cannot be after today.';
  }
  if (fromDate > toDate) {
    return 'From date must be on or before To date.';
  }
  return null;
};

const PERIOD_OPTIONS: { value: TestAccuracyPeriod; label: string }[] = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: 'custom', label: 'Custom' },
];

const SECTION_OPTIONS: { value: TestAccuracySection; label: string }[] = [
  { value: 'verification', label: 'Verification' },
  { value: 'execution', label: 'Execution' },
];

interface ISectionCacheEntry {
  summary: ITestAccuracySummary;
  days: ITestAccuracyDay[];
}

const buildFilterKey = (params: {
  orgId: string;
  projectId: string;
  testType: string;
  source: string;
  period: TestAccuracyPeriod;
  fromDate: string;
  toDate: string;
}) =>
  JSON.stringify({
    orgId: params.orgId,
    projectId: params.projectId,
    testType: params.testType,
    source: params.source,
    period: params.period,
    fromDate: params.fromDate,
    toDate: params.toDate,
  });

const DashboardPage = () => {
  const [orgs, setOrgs] = useState<ITestAccuracyOrg[]>([]);
  const [projects, setProjects] = useState<ITestAccuracyProject[]>([]);
  const [orgId, setOrgId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [testType, setTestType] = useState('');
  const [source, setSource] = useState('');
  const [period, setPeriod] = useState<TestAccuracyPeriod>('7');
  /** Custom range used for API calls (set only when the user clicks Search in the date-range popover). */
  const [appliedCustomRange, setAppliedCustomRange] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [activeSection, setActiveSection] = useState<TestAccuracySection>('verification');

  const [filterCacheKey, setFilterCacheKey] = useState<string | null>(null);
  const [sectionCache, setSectionCache] = useState<
    Partial<Record<TestAccuracySection, ISectionCacheEntry>>
  >({});
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [filterError, setFilterError] = useState<string | null>(null);

  const { register, watch, setValue, formState } = useForm<IDashboardDateForm>({
    defaultValues: { dateFrom: '', dateTo: '' },
    mode: 'onChange',
  });
  const dateFrom = watch('dateFrom');
  const dateTo = watch('dateTo');

  const abortRef = useRef<AbortController | null>(null);
  const activeSectionRef = useRef(activeSection);

  activeSectionRef.current = activeSection;

  const isAllOrgs = orgId === ALL_ORGS_ID;

  const currentFilterKey = useCallback(() => {
    const { from, to } = periodDates(period, appliedCustomRange?.from, appliedCustomRange?.to);
    return buildFilterKey({
      orgId,
      projectId,
      testType,
      source,
      period,
      fromDate: from,
      toDate: to,
    });
  }, [orgId, projectId, testType, source, period, appliedCustomRange]);

  const fetchSection = useCallback(
    async (section: TestAccuracySection, signal: AbortSignal): Promise<ISectionCacheEntry> => {
      const { from, to } = periodDates(period, appliedCustomRange?.from, appliedCustomRange?.to);

      let summary: ITestAccuracySummary = {};
      const days: ITestAccuracyDay[] = [];
      let sawDay = false;

      await testAccuracyService.streamDaily(
        {
          org_id: orgId,
          project_id: isAllOrgs ? undefined : projectId || undefined,
          test_type: testType || undefined,
          source: source || undefined,
          from_date: from || undefined,
          to_date: to || undefined,
          section,
        },
        {
          meta: () => {
            setLoading(false);
          },
          summary: (evt) => {
            summary = evt.summary;
          },
          day: (evt) => {
            sawDay = true;
            days.push(evt.day);
          },
          done: () => {
            if (!sawDay) {
              days.length = 0;
            }
          },
        },
        signal
      );

      return { summary, days };
    },
    [orgId, isAllOrgs, projectId, testType, source, period, appliedCustomRange]
  );

  useEffect(() => {
    let cancelled = false;
    const loadOrgs = async () => {
      setLoadingOrgs(true);
      const data = await testAccuracyService.getOrgs();
      if (!cancelled) {
        setOrgs(data.orgs || []);
        setLoadingOrgs(false);
      }
    };
    void loadOrgs();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const orgOptions = useMemo(
    () =>
      orgs
        .filter((org) => org.org_id !== ALL_ORGS_ID)
        .map((org) => ({
          value: org.org_id,
          label: `${org.org_name} (${org.org_id})`,
        })),
    [orgs]
  );

  const projectOptions = useMemo(
    () => [
      { value: '', label: 'All projects' },
      ...projects.map((project) => ({
        value: project.project_id,
        label: project.code ? `${project.project_name} (${project.code})` : project.project_name,
      })),
    ],
    [projects]
  );

  const testTypeOptions = useMemo(() => [...TEST_TYPE_OPTIONS], []);
  const sourceOptions = useMemo(() => [...SOURCE_OPTIONS], []);

  const handleOrgChange = async (value: string) => {
    setOrgId(value);
    setProjectId('');
    setProjects([]);
    setFilterError(null);
    setHasLoaded(false);
    setFilterCacheKey(null);
    setSectionCache({});
    if (!value || value === ALL_ORGS_ID) {
      return;
    }
    const data = await testAccuracyService.getProjects(value);
    setProjects(data.projects || []);
  };

  const handlePeriodChange = (next: TestAccuracyPeriod) => {
    setPeriod(next);
    setFilterError(null);
    if (next !== 'custom') {
      setAppliedCustomRange(null);
      setValue('dateFrom', '');
      setValue('dateTo', '');
    }
  };

  const loadSection = useCallback(
    async (section: TestAccuracySection, replaceCache: boolean) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);

      try {
        const data = await fetchSection(section, controller.signal);
        const nextKey = currentFilterKey();

        setFilterCacheKey(nextKey);
        setHasLoaded(true);
        setSectionCache((prev) =>
          replaceCache ? { [section]: data } : { ...prev, [section]: data }
        );
        setFilterError(null);
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          return;
        }
        setFilterError((error as Error).message || 'Failed to load metrics.');
        if (replaceCache) {
          setHasLoaded(false);
          setFilterCacheKey(null);
          setSectionCache({});
        }
      } finally {
        setLoading(false);
      }
    },
    [currentFilterKey, fetchSection]
  );

  const handleSectionChange = (next: TestAccuracySection) => {
    setActiveSection(next);
    setFilterError(null);

    if (!hasLoaded || filterCacheKey !== currentFilterKey()) {
      return;
    }

    if (sectionCache[next]) {
      return;
    }

    void loadSection(next, false);
  };

  const handleCustomRangeSearch = useCallback(() => {
    const dateError = getCustomDateValidationError(dateFrom, dateTo);
    if (dateError) {
      setFilterError(dateError);
      return;
    }
    setFilterError(null);
    setAppliedCustomRange({ from: dateFrom, to: dateTo });
  }, [dateFrom, dateTo]);

  const handleCustomRangeClear = useCallback(() => {
    setAppliedCustomRange(null);
    setValue('dateFrom', '');
    setValue('dateTo', '');
    setPeriod('7');
    setFilterError(null);
  }, [setValue]);

  // Auto-load after filters change — only once an organization is selected
  // (and, for a custom period, only once a range has been applied via Search).
  useEffect(() => {
    if (loadingOrgs || !orgId) {
      return;
    }

    if (period === 'custom' && !appliedCustomRange) {
      return;
    }

    const timer = window.setTimeout(() => {
      void loadSection(activeSectionRef.current, true);
    }, AUTO_LOAD_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [orgId, projectId, testType, source, period, appliedCustomRange, loadingOrgs, loadSection]);

  const activeData =
    hasLoaded && filterCacheKey === currentFilterKey() ? sectionCache[activeSection] : undefined;

  return (
    <div className='px-6 pt-3 pb-6 bg-gray-50 min-h-full'>
      <div className='mb-6'>
        <p className='text-sm text-gray-500 m-0'>
          Test Accuracy - Daily Verification and Execution Metrics
        </p>
      </div>

      <Panel titleSpacing='loose'>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
          <SearchableSelect
            variant='filter'
            label='Organization'
            options={orgOptions}
            value={orgId}
            onChange={(value) => {
              void handleOrgChange(value);
            }}
            placeholder={loadingOrgs ? 'Loading orgs…' : 'Select organization'}
            disabled={loadingOrgs}
          />
          <SearchableSelect
            variant='filter'
            label='Project'
            options={projectOptions}
            value={projectId}
            onChange={(value) => {
              setProjectId(value);
              setFilterError(null);
            }}
            placeholder={!orgId ? 'Select organization first' : 'All projects'}
            disabled={!orgId || isAllOrgs}
          />
          <SearchableSelect
            variant='filter'
            label='Test Type'
            options={testTypeOptions}
            value={testType}
            onChange={(value) => {
              setTestType(value);
              setFilterError(null);
            }}
            placeholder='All'
          />
          <SearchableSelect
            variant='filter'
            label='Source'
            options={sourceOptions}
            value={source}
            onChange={(value) => {
              setSource(value);
              setFilterError(null);
            }}
            placeholder='All'
          />
        </div>

        <div className='mt-4 flex flex-wrap items-end gap-4'>
          <SegmentedControl
            label='Time period'
            options={PERIOD_OPTIONS}
            value={period}
            onChange={handlePeriodChange}
          />

          {period === 'custom' ? (
            <DateRangeFilter<IDashboardDateForm>
              register={register}
              errors={formState.errors}
              dateFromName='dateFrom'
              dateToName='dateTo'
              dateFrom={dateFrom}
              dateTo={dateTo}
              onSearch={handleCustomRangeSearch}
              onClear={handleCustomRangeClear}
            />
          ) : null}

          <div className='ml-auto'>
            <SegmentedControl
              label='Metric'
              options={SECTION_OPTIONS}
              value={activeSection}
              onChange={handleSectionChange}
              bordered
            />
          </div>
        </div>

        {filterError ? <p className='mt-3 mb-0 text-sm text-red-600'>{filterError}</p> : null}
      </Panel>

      {loading && !activeData ? (
        <p className='text-sm text-gray-500 text-center py-8'>Loading…</p>
      ) : null}

      {activeData ? (
        <>
          <TestAccuracySummary
            section={activeSection}
            verification={activeData.summary.verification}
            execution={activeData.summary.execution}
          />
          <TestAccuracyDailyTable days={activeData.days} section={activeSection} />
        </>
      ) : null}
    </div>
  );
};

export default DashboardPage;
